import type { Event } from '@bugsnag/js'
import { getActivePinia } from 'pinia'
import { isNavigationFailure, NavigationFailureType } from 'vue-router'
import { useDialogsStore } from '@/store/dialogs'
import type { NativeAppInfo } from '@/interfaces'
import { formatNativeAppVersion } from './native-app-info'

const ignoreThirdPartyFetchErrors = (event: Event): boolean => {
  const error = event.errors[0]

  if (error?.errorMessage !== 'Failed to fetch') {
    return false
  }

  if (!error.stacktrace || !Array.isArray(error.stacktrace)) {
    return false
  }

  return error.stacktrace.some(
    (frame) => frame.file && frame.file.includes('heap-api.com')
  )
}

// An aborted, cancelled or duplicate navigation is a router outcome, not an
// error, but reaches Bugsnag as an empty-message unhandled rejection. Without
// a type, isNavigationFailure also matches real bugs like an unknown route name.
const IGNORED_NAVIGATION_FAILURES = [
  NavigationFailureType.aborted,
  NavigationFailureType.cancelled,
  NavigationFailureType.duplicated
]

const ignoreNavigationFailures = (event: Event): boolean =>
  IGNORED_NAVIGATION_FAILURES.some((type) =>
    isNavigationFailure(event.originalError, type)
  )

const CHUNK_LOAD_ERROR_PATTERNS = [
  'failed to fetch dynamically imported module',
  'importing a module script failed',
  'error loading dynamic',
  'networkerror when attempting to fetch resource',
  'load failed'
]

const handleVueAsyncComponentLoaderErrors = (event: Event): void => {
  const error = event.errors[0]
  const message = error?.errorMessage?.toLowerCase() ?? ''

  const isChunkLoadError = CHUNK_LOAD_ERROR_PATTERNS.some((pattern) =>
    message.includes(pattern)
  )
  if (!isChunkLoadError) {
    return
  }

  const metadataSource = event as {
    getMetadata?: (section: string) => Record<string, unknown> | undefined
    metadata?: Record<string, Record<string, unknown>>
  }
  const vueMetadata =
    metadataSource.getMetadata?.('vue') ?? metadataSource.metadata?.vue
  const component =
    typeof vueMetadata?.component === 'string' ? vueMetadata.component : ''
  const errorInfo =
    typeof vueMetadata?.errorInfo === 'string' ? vueMetadata.errorInfo : ''

  if (
    component === 'AsyncComponentWrapper' &&
    errorInfo.toLowerCase() === 'async component loader'
  ) {
    event.unhandled = false
    event.severity = 'info'
    event.context = 'async-component:loader'
    event.addMetadata('asyncComponent', {
      handled: true,
      message: 'Async component failed to load after retries'
    })
  }
}

export const reclassifyBugsnagEvent = (event: Event): void => {
  // TODO once we've proved we're catching all the cases
  // consider filtering out these error messages from bugnsag completely
  // instead of reclassifying them
  handleVueAsyncComponentLoaderErrors(event)
}

export function bugsnagErrorFilter(event: Event): boolean {
  return !ignoreThirdPartyFetchErrors(event) && !ignoreNavigationFailures(event)
}

export function attachOpenDialogMetadata(event: Event): void {
  if (!getActivePinia()) {
    return
  }
  const { openDialogName } = useDialogsStore()
  if (!openDialogName) {
    return
  }
  event.addMetadata('dialog', { open: openDialogName })
}

let nativeAppVersion: string | undefined

export function setNativeAppVersion(info: NativeAppInfo): void {
  nativeAppVersion = formatNativeAppVersion(info)
}

export function applyNativeAppVersion(event: Event): void {
  if (!nativeAppVersion) return

  event.app.version = nativeAppVersion
}

export function resetNativeAppVersion(): void {
  nativeAppVersion = undefined
}
